"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateDirectInsertBorrowingBaseReport = exports.isClassLabel = void 0;
const lodash_1 = __importDefault(require("lodash"));
const uuid_1 = require("uuid");
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const borrowingBase_1 = require("./borrowingBase");
const isClassLabel = (test) => {
    const isValid = lodash_1.default.includes(lodash_1.default.values(finley_types_1.CreditFacility.ClassLabel), test);
    return isValid;
};
exports.isClassLabel = isClassLabel;
const assertUniqueKeys = (keys) => {
    const duplicateKeySet = new Set();
    const seenKeySet = new Set();
    keys.forEach((key) => {
        if (!seenKeySet.has(key)) {
            seenKeySet.add(key);
        }
        else {
            duplicateKeySet.add(key);
        }
    });
    (0, common_1.finleyAssert)(duplicateKeySet.size === 0, `Duplicate [keys:${Array.from(duplicateKeySet).join(',')}] detected during direct borrowing base insert.`);
};
/**
 * This method is intended to mirror calculateBorrowingBaseReport in borrowingBase.ts
 * in terms of functionality
 */
const calculateDirectInsertBorrowingBaseReport = async ({ borrowingBaseMechanics, bankAccountCashBalances, settlementDate, currentPrincipalBalance, isBorrowingBaseReservePositiveExcessCollectionsOverReserve, creditFacilitySlug, creditFacilityId, creditAgreementId, creditAgreementVersion, commitmentAmount, reportingDate, fileVersion, organizationId, directInsertBorrowingBaseReportRecords, directInsertClassDetails, directInsertConcentrationLimitRecords, directInsertEligibilityTestRecords, directInsertMultiConcentrationLimitRecords, }) => {
    assertUniqueKeys(directInsertMultiConcentrationLimitRecords.map((record) => `${record.loanProductId}-${record.concentrationLimitKey}`));
    // directInsertClassDetails is already an object keyed on classLabel
    /**
     * borrowing base report records
     */
    assertUniqueKeys(directInsertBorrowingBaseReportRecords.map((record) => record.loanProductId));
    const loanProductIds = borrowingBaseMechanics.loanProducts.map((lp) => lp.loanProductId);
    const seenLoanProductIds = [];
    const [validDirectInsertBorrowingBaseReportRecords, invalidDirectInsertBorrowingBaseReportRecords,] = lodash_1.default.partition(directInsertBorrowingBaseReportRecords, (record) => {
        seenLoanProductIds.push(record.loanProductId);
        return loanProductIds.includes(record.loanProductId);
    });
    const missingDirectInsertBorrowingBaseReportLoanProductIds = lodash_1.default.difference(loanProductIds, seenLoanProductIds);
    missingDirectInsertBorrowingBaseReportLoanProductIds.forEach((loanProductId) => {
        validDirectInsertBorrowingBaseReportRecords.push({
            loanProductId,
            totalReceivableExposure: 0,
            assetValuation: 0,
            totalEligibleExposure: 0,
            totalEligibilityKickout: 0,
            eligibleExposureLessConcentrationKickouts: 0,
            totalConcentrationKickout: 0,
            borrowingBaseAmount: 0,
            performingExposure: 0,
        });
    });
    /**
     * elig records
     */
    assertUniqueKeys(directInsertEligibilityTestRecords.map((record) => record.eligibilityCriteriaKey));
    const eligibilityKeys = borrowingBaseMechanics.loanProducts.flatMap((lp) => {
        return lp.eligibilityTestConfigs.map((test) => {
            return test.propertyKey;
        });
    });
    const seenEligibilityKeys = [];
    const [validDirectInsertEligibilityTestRecords, invalidDirectInsertEligibilityTestRecords,] = lodash_1.default.partition(directInsertEligibilityTestRecords, (record) => {
        seenEligibilityKeys.push(record.eligibilityCriteriaKey);
        return eligibilityKeys.includes(record.eligibilityCriteriaKey);
    });
    const missingDirectInsertEligibilityTestRecords = lodash_1.default.difference(eligibilityKeys, seenEligibilityKeys);
    missingDirectInsertEligibilityTestRecords.forEach((eligibilityCriteriaKey) => {
        validDirectInsertEligibilityTestRecords.push({
            eligibilityCriteriaKey,
            eligibilityReduction: 0,
        });
    });
    /**
     * conc limits
     */
    assertUniqueKeys(directInsertConcentrationLimitRecords.map((record) => record.concentrationLimitKey));
    const concentrationLimitKeys = borrowingBaseMechanics.loanProducts.flatMap((lp) => lp.concentrationLimitConfigs.map((test) => test.propertyKey));
    const multiConcentrationLimitKeys = borrowingBaseMechanics.multiLoanProductConcentrationLimits.map((test) => test.propertyKey);
    const allConcentrationLimitKeys = [
        ...concentrationLimitKeys,
        ...multiConcentrationLimitKeys,
    ];
    const seenConcentrationLimitKeys = [];
    const [validDirectInsertConcentrationLimitRecords, invalidDirectInsertConcentrationLimitRecords,] = lodash_1.default.partition(directInsertConcentrationLimitRecords, (record) => {
        seenConcentrationLimitKeys.push(record.concentrationLimitKey);
        return allConcentrationLimitKeys.includes(record.concentrationLimitKey);
    });
    const missingDirectInsertConcentrationLimitRecords = lodash_1.default.difference(allConcentrationLimitKeys, seenConcentrationLimitKeys);
    missingDirectInsertConcentrationLimitRecords.forEach((concentrationLimitKey) => {
        validDirectInsertConcentrationLimitRecords.push({
            concentrationLimitKey,
            concentrationLimit: 0,
            concentrationAmount: 0,
            totalEligibleExposure: 0,
            concentration: 0,
            borrowingBaseReduction: 0,
            complianceStatus: 'COMPLIANT',
        });
    });
    // we don't expect MultiConcentrationLimitRecords to be populated, only for
    // records that have excess that need to be reported
    const [validDirectInsertMultiConcentrationLimitRecords, invalidDirectInsertMultiConcentrationLimitRecords,] = lodash_1.default.partition(directInsertMultiConcentrationLimitRecords, (record) => allConcentrationLimitKeys.includes(record.concentrationLimitKey));
    /**
     * Aggregrations of loan products
     */
    const borrowingBaseReportId = (0, uuid_1.v4)();
    const totalReceivableExposure = validDirectInsertBorrowingBaseReportRecords.reduce((acc, { totalReceivableExposure: receivableExposure }) => acc + receivableExposure, 0);
    const totalAssetValuation = validDirectInsertBorrowingBaseReportRecords.reduce((acc, { assetValuation }) => acc + assetValuation, 0);
    const totalEligibleExposure = validDirectInsertBorrowingBaseReportRecords.reduce((acc, { totalEligibleExposure: eligibleExposure }) => acc + eligibleExposure, 0);
    const totalEligibilityKickout = validDirectInsertBorrowingBaseReportRecords.reduce((acc, { totalEligibilityKickout: eligibilityKickout }) => acc + eligibilityKickout, 0);
    const totalConcentrationKickout = validDirectInsertBorrowingBaseReportRecords.reduce((acc, { totalConcentrationKickout: conentrationKickout }) => acc + conentrationKickout, 0);
    // legacy borrowingBaseAmountAssets === eligibleExposureLessConcentrationKickouts in new domain
    const totalEligibleExposureLessConcentrationKickouts = directInsertBorrowingBaseReportRecords.reduce((acc, { eligibleExposureLessConcentrationKickouts }) => acc + eligibleExposureLessConcentrationKickouts, 0);
    // legacy maxReceivableAdvanceAmount === borrowingBaseAmount in new domain
    const totalBorrowingBaseAmount = validDirectInsertBorrowingBaseReportRecords.reduce((acc, { borrowingBaseAmount }) => acc + borrowingBaseAmount, 0);
    const totalPerformingExposure = validDirectInsertBorrowingBaseReportRecords.reduce((acc, { performingExposure }) => acc + performingExposure, 0);
    const eligibilityCriteriaCalculations = validDirectInsertEligibilityTestRecords.map((record) => ({
        ...record,
        id: (0, uuid_1.v4)(),
        borrowingBaseReportId,
        created_at: new Date(),
        updated_at: new Date(),
    }));
    const concentrationLimitCalculations = validDirectInsertConcentrationLimitRecords.map((record) => {
        const defaultComplianceStatus = record.borrowingBaseReduction === 0 ? 'COMPLIANT' : 'LIMIT_REACHED';
        return {
            ...record,
            id: (0, uuid_1.v4)(),
            borrowingBaseReportId,
            complianceStatus: record.complianceStatus
                ? record.complianceStatus
                : defaultComplianceStatus,
            created_at: new Date(),
            updated_at: new Date(),
        };
    });
    const concentrationLimitMultiLoanProductCalculations = validDirectInsertMultiConcentrationLimitRecords.map((record) => {
        const concentrationLimitCalculation = concentrationLimitCalculations.find((calcs) => calcs.concentrationLimitKey === record.concentrationLimitKey);
        (0, common_1.finleyAssert)(concentrationLimitCalculation, `Unable to find [concentrationLimitKey:${record.concentrationLimitKey}] while formating multi-concentration limit`);
        return {
            id: (0, uuid_1.v4)(),
            concentrationLimitCalculationId: concentrationLimitCalculation.id,
            concentrationLimitKey: record.concentrationLimitKey,
            borrowingBaseReduction: record.borrowingBaseReduction,
            borrowingBaseReportId,
            loanProductId: record.loanProductId,
            created_at: new Date(),
            updated_at: new Date(),
        };
    });
    const cashAdvanceRate = (0, borrowingBase_1.calculateEffectiveCashAdvanceRate)(bankAccountCashBalances);
    const borrowingBaseInsights = (0, borrowingBase_1.generateBorrowingBaseInsightCalcs)({
        bankAccountCashBalances,
        assetValuation: totalAssetValuation,
        maxReceivableAdvanceAmount: totalBorrowingBaseAmount,
        settlementDate,
        currentPrincipalBalance,
        borrowingBaseAmountAssets: totalEligibleExposureLessConcentrationKickouts,
        commitmentAmount,
        reportingDate,
        totalReceivableExposure,
    });
    const effectiveReceivableValuationRate = (0, borrowingBase_1.calcEffectiveReceivableValuationRate)({
        assetValuation: totalAssetValuation,
        totalReceivableExposure,
    });
    const borrowingBaseReportLoanProductCalculations = directInsertBorrowingBaseReportRecords.map((record) => {
        const effectiveReceivableAdvanceRate = (0, borrowingBase_1.calcEffectiveReceivableAdvanceRate)({
            totalReceivableBorrowingBaseAmount: record.borrowingBaseAmount,
            totalReceivableExposure: record.totalReceivableExposure,
        });
        const loanProductEffectiveReceivableValuationRate = (0, borrowingBase_1.calcEffectiveReceivableValuationRate)({
            assetValuation: record.assetValuation,
            totalReceivableExposure: record.totalReceivableExposure,
        });
        const weightedAverageReceivableAdvanceRate = (0, borrowingBase_1.calcWeightedAverageReceivableAdvanceRate)({
            totalReceivableBorrowingBaseAmount: record.borrowingBaseAmount,
            totalEligibleExposureLessConcentrationKickouts: record.eligibleExposureLessConcentrationKickouts,
        });
        return {
            borrowingBaseReportId,
            creditFacilityId,
            loanProductId: record.loanProductId,
            totalReceivableExposure: record.totalReceivableExposure,
            totalEligibilityKickout: record.totalEligibilityKickout,
            totalEligibleExposure: record.totalEligibleExposure,
            totalConcentrationKickout: record.totalConcentrationKickout,
            totalEligibleExposureLessConcentrationKickouts: record.eligibleExposureLessConcentrationKickouts,
            totalReceivableBorrowingBaseAmount: record.borrowingBaseAmount,
            weightedAverageReceivableAdvanceRate,
            effectiveReceivableAdvanceRate,
            effectiveReceivableValuationRate: loanProductEffectiveReceivableValuationRate,
        };
    });
    // *** Class Detail Mechanics ***
    const invalidDirectInsertClassDetails = [];
    const borrowingBaseClassDetails = {
        classAttachmentLimits: null,
    };
    if (directInsertClassDetails) {
        borrowingBaseClassDetails.classAttachmentLimits = Object.entries(directInsertClassDetails).reduce((acc, [classLabel, attachmentRateLimit]) => {
            if ((0, exports.isClassLabel)(classLabel)) {
                acc.push({ classLabel, attachmentRateLimit });
                // special case to ignore tape sanitizer properties
            }
            else if (classLabel !== 'finley_sanitized') {
                invalidDirectInsertClassDetails.push(classLabel);
            }
            return acc;
        }, []);
    }
    const borrowingBaseReport = {
        ...borrowingBaseInsights,
        id: borrowingBaseReportId,
        borrowerId: organizationId,
        creditAgreementVersion,
        creditFacilityId,
        creditAgreementId,
        creditFacilitySlug,
        fileVersion,
        reportingDate,
        created_at: new Date(),
        updated_at: new Date(),
        cashAdvanceRate,
        commitmentAmount,
        isBorrowingBaseReservePositiveExcessCollectionsOverReserve,
        effectiveReceivableValuationRate,
        // calculate values
        totalEligibleExposure,
        totalReceivableExposure,
        borrowingBaseAmountAssets: totalEligibleExposureLessConcentrationKickouts,
        maxReceivableAdvanceAmount: totalBorrowingBaseAmount,
        totalConcentrationKickout,
        totalEligibilityKickout,
        // these are updated later in insertBorrowingBaseReportAndCreateProgrammaticFundingRequest => updateReserveAmounts.
        cashReserveAmount: null,
        borrowingBaseReserveAmount: null,
        totalCollectionsAccountCash: null,
        totalPreAdvanceCashContribution: null,
        totalCashBorrowingBaseAmount: null,
        effectiveCashAdvanceRate: null,
        assignedEligibleExposureSinceLastApprovedFundingRequest: null,
        classDetails: borrowingBaseClassDetails,
    };
    return {
        borrowingBaseReport,
        eligibilityCriteriaCalculations,
        concentrationLimitCalculations,
        concentrationLimitMultiLoanProductCalculations,
        borrowingBaseReportLoanProductCalculations,
        totalPerformingExposure,
        invalidDirectInsertBorrowingBaseReportRecords,
        invalidDirectInsertEligibilityTestRecords,
        invalidDirectInsertConcentrationLimitRecords,
        invalidDirectInsertMultiConcentrationLimitRecords,
        invalidDirectInsertClassDetails,
        missingDirectInsertBorrowingBaseReportLoanProductIds,
        missingDirectInsertEligibilityTestRecords,
        missingDirectInsertConcentrationLimitRecords,
    };
};
exports.calculateDirectInsertBorrowingBaseReport = calculateDirectInsertBorrowingBaseReport;
//# sourceMappingURL=directInsertBorrowingBase.js.map